import type { UseQueryResult } from "@tanstack/react-query";
import { CalendarDays } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { formatCredentialTimestamp } from "../../lib/format-date";
import { AcceptWithoutEvidenceDialog } from "./run-report-dialog/accept-without-evidence-dialog";
import { ReopenCarriedDecisionDialog } from "./run-report-dialog/reopen-carried-decision-dialog";
import { ItemSummaryStrip } from "./run-report-dialog/item-summary-strip";
import { getReportIdentity } from "./run-report-dialog/report-identity";
import { ReportItems } from "./run-report-dialog/report-items";
import { useReportReview } from "./run-report-dialog/use-report-review";
import {
  RunFindingSummary,
  RunStatusBadge,
  runFindingLabels,
} from "./run-summary";
import {
  isRunActive,
  type RunReport,
  type RenderRunActivity,
  type RunScope,
  type StartedRun,
} from "./types";

const KEYBOARD_HINTS = "J/K navigate · Enter expand · A accept · C concern";

export function RunReportDialog({
  query,
  scope,
  providerName,
  request,
  canEdit,
  onClose,
  onRestoreFocus,
  renderRunActivity,
}: {
  query: UseQueryResult<RunReport, unknown>;
  scope: RunScope;
  renderRunActivity?: RenderRunActivity;
  providerName?: string;
  request?: StartedRun;
  /** False for read-only reports, such as a frozen affiliation. */
  canEdit: boolean;
  onClose: () => void;
  onRestoreFocus: () => void;
}) {
  const data = query.data;
  // The API rejects review writes on a run that a newer run replaced.
  const isReplaced = canEdit && data?.isLatestRun === false;
  const canEditReport = canEdit && data?.isLatestRun === true;
  const review = useReportReview({ data, scope, canEdit: canEditReport });
  const identity = getReportIdentity({
    providerName,
    identity: data?.identity,
  });
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <DialogContent
        className="flex max-h-[90dvh] w-full max-w-4xl flex-col gap-0 overflow-hidden p-0"
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          onRestoreFocus();
        }}
      >
        <div
          className="flex min-h-0 flex-1 flex-col"
          onKeyDown={review.onKeyDown}
        >
          <DialogHeader className="shrink-0 border-b px-5 py-5 pr-14 text-left sm:px-8 sm:py-6 sm:pr-16">
            {/* The action sits at the header's bottom edge, beside the review
                status it changes, rather than competing with the title. */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
              <div className="min-w-0 space-y-1">
                <DialogTitle className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Primary source report
                </DialogTitle>
                {identity.primaryName && (
                  <h3 className="break-words text-2xl font-bold leading-tight tracking-tight text-foreground sm:text-3xl">
                    {identity.primaryName}
                  </h3>
                )}
                <DialogDescription className="flex flex-wrap gap-x-5 gap-y-1 pt-1 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1.5">
                    {data && <CalendarDays className="size-4" aria-hidden />}
                    {data
                      ? `Requested ${formatCredentialTimestamp(data.run.createdAt)}`
                      : "Source checks and findings for the selected run."}
                  </span>
                  {identity.npi && (
                    <span className="flex items-center gap-1.5">
                      NPI: {identity.npi}
                    </span>
                  )}
                </DialogDescription>
                {identity.additionalNames.length > 0 && (
                  <p className="break-words text-xs leading-5 text-muted-foreground">
                    Other recorded names: {identity.additionalNames.join("; ")}
                  </p>
                )}
                {data && (
                  <ReportStatus
                    data={data}
                    renderRunActivity={renderRunActivity}
                  />
                )}
              </div>
              {data && canEditReport && (
                <Button
                  className="shrink-0 self-start sm:self-end"
                  disabled={
                    review.validOpenItems.length === 0 ||
                    review.decisions.isAcceptingAll
                  }
                  onClick={review.acceptAllValid}
                >
                  Accept all valid
                </Button>
              )}
            </div>
          </DialogHeader>
          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5 sm:px-8 sm:py-6">
            {query.isLoading && (
              <p role="status" className="text-sm text-muted-foreground">
                Loading verification report…
              </p>
            )}
            {query.isError && (
              <div role="alert" className="space-y-2 text-sm text-destructive">
                <p>
                  Couldn’t load this report. Your saved run is still available.
                </p>
                <Button
                  variant="surface"
                  disabled={query.isFetching}
                  onClick={() => void query.refetch()}
                >
                  Retry report
                </Button>
              </div>
            )}
            {request?.dispatchPending && (!data || isRunActive(data.run)) && (
              <p role="status" className="mb-4 text-sm text-muted-foreground">
                Your request was saved and is waiting to start. It will retry
                automatically.
              </p>
            )}
            {request?.reused && (!data || isRunActive(data.run)) && (
              <p className="mb-4 text-sm text-muted-foreground">
                Showing the verification already in progress for this provider.
              </p>
            )}
            {isReplaced && (
              <p className="mb-4 text-sm text-muted-foreground">
                A newer run replaced this one. Its review decisions are
                read-only.
              </p>
            )}
            {data && (
              <ReportItems
                data={data}
                scope={scope}
                canEdit={canEditReport}
                review={review}
              />
            )}
          </div>
          {data && canEditReport && (
            <p className="shrink-0 border-t bg-slate-50 px-5 py-2 text-xs text-muted-foreground sm:px-8">
              {KEYBOARD_HINTS}
            </p>
          )}
        </div>
        <AcceptWithoutEvidenceDialog
          open={review.confirmItem !== null}
          onUpload={review.requestUpload}
          onAcceptAnyway={review.acceptAnyway}
          onCancel={review.cancelConfirm}
        />
        {review.reopenItem && (
          <ReopenCarriedDecisionDialog
            onReopen={review.reopen}
            onCancel={review.cancelReopen}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

/** While sources run, the workspace's activity line; afterwards, how many
 * items staff have decided. It sits in the header so it stays visible while
 * the items scroll. */
function ReportStatus({
  data,
  renderRunActivity,
}: {
  data: RunReport;
  renderRunActivity?: RenderRunActivity;
}) {
  if (!isRunActive(data.run)) {
    return <ItemSummaryStrip summary={data.itemSummary} items={data.items} />;
  }
  const activity = renderRunActivity?.({
    run: data.run,
    summary: data.summary,
    checks: data.checks,
    itemSummary: data.itemSummary,
  });
  return (
    <div className="space-y-2 pt-3">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        {activity?.badge ?? <RunStatusBadge summary={data.summary} />}
        {(!activity || runFindingLabels(data.summary).length > 0) && (
          <RunFindingSummary summary={data.summary} />
        )}
      </div>
      {activity?.details}
    </div>
  );
}
